import React from 'react';

import { Input } from '@/component-library/widgets/Input/Input';
import { QuickFilterCriteria } from '@/component-library/widgets/QuickFilter/QuickFilter';

interface SegmentFilterFormProps {
  criteria: QuickFilterCriteria;
  onCriteriaChange: (criteria: QuickFilterCriteria) => void;
}

const SegmentFilterForm: React.FC<SegmentFilterFormProps> = ({ criteria, onCriteriaChange }) => {
  const handleChange = (field: keyof QuickFilterCriteria, value: any) => {
    onCriteriaChange({
      ...criteria,
      [field]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-primary-0 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-primary-400 mb-4">Define Segment Criteria</h3>
        <p className="text-xs text-gray-300 mb-6">
          Configure filters to define which users belong to this segment. All selected criteria will be combined with
          AND logic.
        </p>

        <div className="space-y-4">
          {/* Risk Level */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Risk Level</label>
            <select
              className="w-full text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={criteria.riskLevel || 'all'}
              onChange={(e) =>
                handleChange(
                  'riskLevel',
                  e.target.value === 'all' ? null : (e.target.value as 'high' | 'medium' | 'low'),
                )
              }
            >
              <option value="all">All Risk Levels</option>
              <option value="high">High Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="low">Low Risk</option>
            </select>
          </div>

          {/* Email Domain */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Email Domain</label>
            <Input
              type="text"
              placeholder="e.g., instagram.com"
              value={criteria.emailDomain || ''}
              onChange={(e) => handleChange('emailDomain', e.target.value.trim() || null)}
            />
            <p className="text-xs text-custom-darkSilver mt-1">Enter domain without @ (e.g., instagram.com)</p>
          </div>

          {/* Risk Score Range */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Risk Score Range</label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="From"
                  value={criteria.riskScoreFrom?.toString() || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value, 10) : null;
                    if (value !== null && (value < 0 || value > 100)) return;
                    handleChange('riskScoreFrom', value);
                  }}
                />
              </div>
              <span className="text-custom-darkSilver">-</span>
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="To"
                  value={criteria.riskScoreTo?.toString() || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value, 10) : null;
                    if (value !== null && (value < 0 || value > 100)) return;
                    handleChange('riskScoreTo', value);
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-custom-darkSilver mt-1">Enter values between 0 and 100</p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Status</label>
            <select
              className="w-full text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={criteria.status || 'all'}
              onChange={(e) => handleChange('status', e.target.value === 'all' ? null : (e.target.value as any))}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="past_due">Past Due</option>
              <option value="past_customer">Past Customer</option>
              <option value="free_trial">Free Trial</option>
              <option value="free">Free</option>
              <option value="new_user">New User</option>
            </select>
          </div>

          {/* Signal Action Type */}
          <div>
            <label className="block text-sm font-medium text-custom-darkSilver mb-2">Recommended Action Type</label>
            <select
              className="w-full text-sm border border-custom-lightSilver rounded px-3 py-2 bg-white text-custom-darkSilver focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={criteria.signalActionType || 'all'}
              onChange={(e) =>
                handleChange('signalActionType', e.target.value === 'all' ? null : (e.target.value as any))
              }
            >
              <option value="all">All Action Types</option>
              <option value="discount">Discount</option>
              <option value="upgrade">Upgrade</option>
              <option value="engagement">Engagement</option>
            </select>
            <p className="text-xs text-custom-darkSilver mt-1">
              Filter users by recommended action type for their signals
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentFilterForm;
